// ランキング。点を出すのは関数（submitScore）だけ。上位はだれでも Firestore から読める。
import { boardDocId, periodKey, type LeaderboardEntry, type Period, type SubmitResult } from "@appful/sdk/protocol";
import { collection, getDocs, limit, orderBy, query } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { onSession, session } from "./auth";
import type { CatalogBoard } from "./catalog";
import { db, functions } from "./firebase";

const TOP_CACHE_MS = 10_000;
const topCache = new Map<string, { at: number; entries: Omit<LeaderboardEntry, "isMe">[]; ids: string[] }>();
const listeners = new Set<(gameId: string, boardId: string) => void>();

// 名前を変えたら、覚えている上位を捨てる（次に読むとき、新しい名前で読み直す）。
// ほかの画面より先に知らせを受けるよう、読み込んだときに登録する
let lastName: string | null = null;
onSession((s) => {
  if (s.displayName !== lastName) {
    lastName = s.displayName;
    topCache.clear();
  }
});

/** 点が入ったら知らせる（一覧の横のランキングを更新するため） */
export function onScoreSubmitted(fn: (gameId: string, boardId: string) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export async function submit(gameId: string, board: CatalogBoard, score: number): Promise<SubmitResult> {
  if (!session().user) return { status: "signin_required" };
  if (!Number.isInteger(score)) return { status: "rejected", reason: "not_integer" };
  if (score < board.min || score > board.max) return { status: "rejected", reason: "out_of_range" };
  try {
    const res = await httpsCallable<unknown, SubmitResult>(functions, "submitScore")({ gameId, boardId: board.id, score });
    for (const p of board.periods) topCache.delete(boardDocId(gameId, board.id, periodKey(p)));
    if (res.data.status === "submitted") listeners.forEach((fn) => fn(gameId, board.id));
    return res.data;
  } catch (e) {
    const code = (e as { code?: string }).code ?? "";
    console.error("[leaderboard] submit failed", e);
    if (code.includes("unavailable") || code.includes("deadline") || !navigator.onLine) return { status: "offline" };
    return { status: "rejected", reason: code || "error" };
  }
}

export async function top(gameId: string, board: CatalogBoard, period: Period, n: number): Promise<LeaderboardEntry[]> {
  const id = boardDocId(gameId, board.id, periodKey(period));
  let cached = topCache.get(id);
  if (!cached || Date.now() - cached.at > TOP_CACHE_MS) {
    const snap = await getDocs(
      query(collection(db, "leaderboards", id, "entries"), orderBy("score", board.order), limit(Math.min(n, 50))),
    );
    cached = {
      at: Date.now(),
      ids: snap.docs.map((d) => d.id),
      entries: snap.docs.map((d, i) => ({
        rank: i + 1,
        displayName: String(d.get("displayName") ?? "Player"),
        score: Number(d.get("score")),
      })),
    };
    topCache.set(id, cached);
  }
  const me = session().user?.uid;
  return cached.entries.slice(0, n).map((e, i) => ({ ...e, isMe: cached!.ids[i] === me }));
}
